import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faArrowLeft, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageHeader from '../../../components/common/PageHeader';
import Spinner from '../../../components/common/Spinner';
import PeopleService from '../../../services/PeopleService';
import CompanyService from '../../../services/CompanyService';
import RoleService from '../../../services/RoleService';
import { useNotification } from '../../../contexts/NotificationContext';
import '../shared/formsResponsive.css';

const initialFormData = {
  fullName: '',
  documentNumber: '',
  email: '',
  phone: '',
  userId: null,
  userEnabled: false,
  companyIds: [],
  companyRoleIds: {},
  enabled: true
};

const PeopleForm = () => {
  const { t } = useTranslation();
  const { success: notifySuccess, error: notifyError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const peopleIdParam = searchParams.get('id');
  const normalizedPeopleId = (() => {
    if (!peopleIdParam) return null;
    const trimmed = String(peopleIdParam).trim().toLowerCase();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  })();
  const isAddMode = location.pathname === '/people-add';

  const [people, setPeople] = useState(initialFormData);
  const [activeTab, setActiveTab] = useState('generalInfo');
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(!isAddMode);
  const [loadingOperation, setLoadingOperation] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [generalErrorMessage, setGeneralErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const filters = { name: '', enabled: '' };
        const data = await CompanyService.getAll(0, 10000, filters);
        setCompanies(data.content || []);
      } catch (error) {
        console.error('Error fetching companies for people form:', error);
        setCompanies([]);
      }
    };

    const fetchRoles = async () => {
      try {
        const filters = { roleName: '', enabled: true };
        const data = await RoleService.getAll(0, 10000, filters);
        setRoles(data.content || []);
      } catch (error) {
        console.error('Error fetching roles for people form:', error);
        setRoles([]);
      }
    };

    fetchCompanies();
    fetchRoles();

    const handleCompanyChange = () => {
      fetchCompanies();
      fetchRoles();
    };

    window.addEventListener('companyChanged', handleCompanyChange);
    return () => window.removeEventListener('companyChanged', handleCompanyChange);
  }, []);

  useEffect(() => {
    if (isAddMode) {
      return;
    }

    if (!normalizedPeopleId) {
      setGeneralErrorMessage(t('common.errorOccurred'));
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setLoadingOperation(true);
      try {
        const response = await PeopleService.getById(normalizedPeopleId);
        setPeople({
          ...initialFormData,
          ...response,
          userId: response?.userId || null,
          userEnabled: !!response?.userEnabled,
          companyIds: response?.companyIds || [],
          companyRoleIds: response?.companyRoleIds || {}
        });
      } catch (error) {
        setGeneralErrorMessage(error.message || t('common.errorOccurred'));
      } finally {
        setIsLoading(false);
        setLoadingOperation(false);
      }
    };

    fetchData();
  }, [isAddMode, normalizedPeopleId, t]);

  useEffect(() => {
    if (generalErrorMessage) {
      notifyError(generalErrorMessage, { autoClose: 3000, placement: 'centered' });
      setGeneralErrorMessage('');
    }
  }, [generalErrorMessage, notifyError]);

  useEffect(() => {
    if (successMessage) {
      notifySuccess(successMessage, { autoClose: 3000, placement: 'centered' });
      setSuccessMessage('');
    }
  }, [successMessage, notifySuccess]);

  const handleInputChange = (field, value) => {
    setPeople((prev) => ({
      ...prev,
      [field]: value
    }));

    if (errorMessages[field]) {
      setErrorMessages((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCompanyToggle = (companyId) => {
    const currentIds = people.companyIds || [];
    const currentRoleIds = people.companyRoleIds || {};
    const newIds = currentIds.includes(companyId)
      ? currentIds.filter((id) => id !== companyId)
      : [...currentIds, companyId];

    const normalizedRoleIds = { ...currentRoleIds };
    if (!newIds.includes(companyId)) {
      delete normalizedRoleIds[companyId];
      delete normalizedRoleIds[String(companyId)];
    }

    setPeople((prev) => ({
      ...prev,
      companyIds: newIds,
      companyRoleIds: normalizedRoleIds
    }));
  };

  const handleCompanyRoleChange = (companyId, roleIdValue) => {
    const parsedRoleId = roleIdValue ? parseInt(roleIdValue, 10) : null;
    const normalizedRoleIds = { ...(people.companyRoleIds || {}) };

    if (parsedRoleId && !Number.isNaN(parsedRoleId)) {
      normalizedRoleIds[companyId] = parsedRoleId;
    } else {
      delete normalizedRoleIds[companyId];
      delete normalizedRoleIds[String(companyId)];
    }

    setPeople((prev) => ({
      ...prev,
      companyRoleIds: normalizedRoleIds
    }));
  };

  const handleUserEnabledChange = async (enabled) => {
    if (isAddMode || !people.id) {
      return;
    }

    setLoadingOperation(true);
    try {
      const updated = await PeopleService.updateUserEnabled(people.id, { enabled });
      setPeople((prev) => ({
        ...prev,
        userId: updated?.userId || prev.userId,
        userEnabled: !!updated?.userEnabled
      }));
    } catch (error) {
      setGeneralErrorMessage(error.message || t('common.errorOccurred'));
    } finally {
      setLoadingOperation(false);
    }
  };

  const handleResetUserPassword = async () => {
    if (isAddMode || !people.id || !people.enabled || !people.userEnabled) {
      return;
    }

    setLoadingOperation(true);
    try {
      const updated = await PeopleService.resetUserPassword(people.id);
      if (updated) {
        setPeople((prev) => ({
          ...prev,
          userId: updated.userId || prev.userId,
          userEnabled: !!updated.userEnabled
        }));
        setSuccessMessage(t('people.success.updated'));
      }
    } catch (error) {
      setGeneralErrorMessage(error.message || t('common.errorOccurred'));
    } finally {
      setLoadingOperation(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!people.fullName || people.fullName.trim() === '') {
      errors.fullName = t('people.errors.fullNameRequired');
    }
    if (!people.email || people.email.trim() === '') {
      errors.email = t('people.errors.emailRequired');
    }

    return errors;
  };

  const buildPayload = () => {
    const companyIds = Array.isArray(people.companyIds)
      ? people.companyIds.map((id) => typeof id === 'string' ? parseInt(id, 10) : id).filter((id) => !Number.isNaN(id))
      : [];

    const companyRoleIds = {};
    if (people.companyRoleIds && typeof people.companyRoleIds === 'object') {
      companyIds.forEach((companyId) => {
        const rawRoleId = people.companyRoleIds[companyId] ?? people.companyRoleIds[String(companyId)];
        const parsedRoleId = typeof rawRoleId === 'string' ? parseInt(rawRoleId, 10) : rawRoleId;
        if (Number.isInteger(parsedRoleId) && parsedRoleId > 0) {
          companyRoleIds[companyId] = parsedRoleId;
        }
      });
    }

    const payload = {
      fullName: people.fullName || '',
      enabled: people.enabled !== undefined ? people.enabled : true,
      companyIds,
      companyRoleIds
    };

    if (people.documentNumber && people.documentNumber.trim()) {
      payload.documentNumber = people.documentNumber.trim();
    }
    if (people.email && people.email.trim()) {
      payload.email = people.email.trim();
    }
    if (people.phone && people.phone.trim()) {
      payload.phone = people.phone.trim();
    }

    return payload;
  };

  const handleSave = async () => {
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setErrorMessages(errors);
      setActiveTab('generalInfo');
      setGeneralErrorMessage(t('validation.pleaseFillRequired'));
      return;
    }

    setLoadingOperation(true);
    setGeneralErrorMessage('');

    try {
      const payload = buildPayload();
      if (isAddMode) {
        const created = await PeopleService.create(payload);
        setPeople({
          ...initialFormData,
          ...created,
          userId: created?.userId || null,
          userEnabled: !!created?.userEnabled,
          companyIds: created?.companyIds || [],
          companyRoleIds: created?.companyRoleIds || {}
        });
        setSuccessMessage(t('people.success.created'));
        if (created?.id) {
          navigate(`/people-edit?id=${created.id}`);
        }
      } else {
        const updated = await PeopleService.update(normalizedPeopleId, payload);
        setPeople({
          ...initialFormData,
          ...updated,
          userId: updated?.userId || null,
          userEnabled: !!updated?.userEnabled,
          companyIds: updated?.companyIds || [],
          companyRoleIds: updated?.companyRoleIds || {}
        });
        setSuccessMessage(t('people.success.updated'));
      }
      setErrorMessages({});
    } catch (error) {
      setGeneralErrorMessage(error.message || t('common.errorOccurred'));
    } finally {
      setLoadingOperation(false);
    }
  };

  const handleCancel = () => {
    navigate('/people');
  };

  if (isLoading) {
    return <Spinner overlay={true} message={t('common.loading')} />;
  }

  return (
    <>
      {loadingOperation && <Spinner overlay={true} message={t('common.processing')} />}

      <PageHeader
        title={isAddMode ? t('people.add') : t('people.edit')}
        subtitle={t('people.subtitle')}
        icon={faUsers}
      >
        <button className="btn btn-primary" onClick={handleCancel}>
          <FontAwesomeIcon icon={faArrowLeft} /> {t('common.goBack')}
        </button>
      </PageHeader>

      <section className="panel">
        <div className="panel-body">
          <div className="tabs">
            <ul className="nav nav-tabs">
              <li className={activeTab === 'generalInfo' ? 'active' : ''}>
                <a href="#people-general-info" onClick={(e) => { e.preventDefault(); setActiveTab('generalInfo'); }} style={{ cursor: 'pointer' }}>
                  {t('people.tabs.generalInfo')}
                </a>
              </li>
              <li className={activeTab === 'companies' ? 'active' : ''}>
                <a href="#people-companies" onClick={(e) => { e.preventDefault(); setActiveTab('companies'); }} style={{ cursor: 'pointer' }}>
                  {t('people.tabs.companies')}
                </a>
              </li>
              <li className={activeTab === 'user' ? 'active' : ''}>
                <a href="#people-user" onClick={(e) => { e.preventDefault(); setActiveTab('user'); }} style={{ cursor: 'pointer' }}>
                  {t('people.tabs.user')}
                </a>
              </li>
            </ul>

            <div className="tab-content">
              {/* Pestaña de información general */}
              {activeTab === 'generalInfo' && (
                <div className="tab-pane active">
                  <div className="form-row-2cols">
                    <div className="form-group">
                      <label htmlFor="field-fullName">
                        <i className="fa fa-user"></i> {t('people.fullName')}
                        <span className="required-field" aria-label="required">*</span>
                      </label>
                      <input
                        id="field-fullName"
                        type="text"
                        className={`form-control ${errorMessages.fullName ? 'has-error' : ''}`}
                        placeholder={t('people.fullNamePlaceholder')}
                        value={people.fullName || ''}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        required
                      />
                      {errorMessages.fullName && <div className="field-error-message">{errorMessages.fullName}</div>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="field-documentNumber">
                        <i className="fa fa-id-card"></i> {t('people.documentNumber')}
                      </label>
                      <input
                        id="field-documentNumber"
                        type="text"
                        className="form-control"
                        placeholder={t('people.documentNumberPlaceholder')}
                        value={people.documentNumber || ''}
                        onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="field-email">
                        <i className="fa fa-envelope"></i> {t('people.email')}
                        <span className="required-field" aria-label="required">*</span>
                      </label>
                      <input
                        id="field-email"
                        type="email"
                        className={`form-control ${errorMessages.email ? 'has-error' : ''}`}
                        placeholder={t('people.emailPlaceholder')}
                        value={people.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                      />
                      {errorMessages.email && <div className="field-error-message">{errorMessages.email}</div>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="field-phone">
                        <i className="fa fa-phone"></i> {t('people.phone')}
                      </label>
                      <input
                        id="field-phone"
                        type="text"
                        className="form-control"
                        placeholder={t('people.phonePlaceholder')}
                        value={people.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Pestaña de usuario */}
              {activeTab === 'user' && (
                <div className="tab-pane active">
                  <div className="form-row-2cols">
                    <div className="form-group">
                      <label htmlFor="field-user-email">
                        <i className="fa fa-envelope"></i> {t('people.email')}
                      </label>
                      <input
                        id="field-user-email"
                        type="text"
                        className="form-control"
                        value={people.email || ''}
                        readOnly
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                        <input
                          id="field-user-enabled"
                          type="checkbox"
                          checked={!!people.userEnabled}
                          disabled={isAddMode || !people.userId}
                          onChange={(e) => handleUserEnabledChange(e.target.checked)}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: isAddMode || !people.userId ? 'not-allowed' : 'pointer',
                            accentColor: '#007bff'
                          }}
                        />
                        <span>{people.userEnabled ? t('people.userEnabled') : t('people.userDisabled')}</span>
                      </label>
                    </div>

                    <div className="form-group">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={isAddMode || !people.id || !people.enabled || !people.userEnabled}
                        onClick={handleResetUserPassword}
                      >
                        {t('people.resetPassword')}
                      </button>
                      <p style={{ color: '#6c757d', fontSize: '12px', marginTop: '8px', marginBottom: 0 }}>
                        {t('people.resetPasswordHelp')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pestaña de empresas */}
              {activeTab === 'companies' && (
                <div className="tab-pane active">
                  <div className="form-group">
                    <label style={{ marginBottom: '10px', display: 'block', fontWeight: '500', color: '#495057' }}>
                      <i className="fa fa-building" style={{ marginRight: '8px' }}></i>
                      {t('people.selectCompanies') || 'Seleccionar Empresas'}
                    </label>
                    <p style={{ color: '#6c757d', fontSize: '13px', marginBottom: '15px', fontStyle: 'italic' }}>
                      {t('people.selectCompaniesHelp') || 'Selecciona una o más empresas para asociar a esta persona. Si no seleccionas ninguna, se relacionará con todas las empresas habilitadas.'}
                    </p>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      padding: '15px',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa'
                    }}>
                      {companies.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#6c757d', padding: '20px', fontStyle: 'italic' }}>
                          {t('people.noCompaniesAvailable') || 'No hay empresas disponibles'}
                        </p>
                      ) : (
                        companies.map((company) => (
                          <div
                            key={company.id}
                            className="people-company-row"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '12px',
                              flexWrap: 'wrap',
                              padding: '12px',
                              backgroundColor: '#ffffff',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              border: '1px solid #dee2e6'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#e9ecef';
                              e.currentTarget.style.borderColor = '#adb5bd';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                              e.currentTarget.style.borderColor = '#dee2e6';
                            }}
                          >
                            <label
                              className="people-company-label"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                margin: 0,
                                cursor: 'pointer',
                                flex: 1,
                                minWidth: 0
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={(people.companyIds || []).includes(company.id)}
                                onChange={() => handleCompanyToggle(company.id)}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  cursor: 'pointer',
                                  accentColor: '#007bff'
                                }}
                              />
                              <span style={{ fontSize: '14px', color: '#495057', fontWeight: '400' }}>
                                {company.name || company.displayName || `Empresa ${company.id}`}
                              </span>
                            </label>

                            <div className="people-company-role">
                              <select
                                className="form-control"
                                value={
                                  (people.companyRoleIds && (
                                    people.companyRoleIds[company.id]
                                    || people.companyRoleIds[String(company.id)]
                                  )) || ''
                                }
                                onChange={(e) => handleCompanyRoleChange(company.id, e.target.value)}
                                disabled={!(people.companyIds || []).includes(company.id)}
                              >
                                <option value="">{t('people.selectRoleOptional', { defaultValue: 'Selecciona un rol (opcional)' })}</option>
                                {roles.map((role) => (
                                  <option key={role.id} value={role.id}>
                                    {role.roleName}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {companies.length > 0 && (
                      <p style={{
                        marginTop: '10px',
                        fontSize: '12px',
                        color: '#6c757d',
                        fontStyle: 'italic'
                      }}>
                        {((people.companyIds || []).length > 0
                          ? `${(people.companyIds || []).length} empresa(s) seleccionada(s)`
                          : 'Ninguna empresa seleccionada (se relacionará con todas)')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="panel-footer" style={{ textAlign: 'right' }}>
          <button type="button" className="btn btn-default" onClick={handleCancel} style={{ marginRight: '10px' }}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave}>
            {t('common.save')}
          </button>
        </div>
      </section>
    </>
  );
};

export default PeopleForm;
